import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  TextField,
} from '@mui/material';
import {
  Delete,
  Download,
  Warning,
  Folder,
  InsertDriveFile,
  ArrowBack,
  FolderRounded,
  Image as ImageIcon,
  Android,
  Close,
  Description,
  Code,
  PictureAsPdf,
  VideoFile,
  AudioFile,
} from '@mui/icons-material';
import { Slide } from '@mui/material';
import { fileManagementApi } from '../services/api';

const FileManager = () => {
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, file: null, confirmText: '' });
  const [error, setError] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, file: null, originalName: null });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFiles();
  }, [currentPath]);

  const fetchFiles = async () => {
    try {
      const response = await fileManagementApi.listFiles(currentPath);
      setFiles(response.files || []);
    } catch (err) {
      setError('Failed to fetch files');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    switch (ext) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return <ImageIcon sx={{ color: '#4CAF50' }} />;
      case 'apk':
        return <Android sx={{ color: '#7CB342' }} />;
      case 'pdf':
        return <PictureAsPdf sx={{ color: '#E53935' }} />;
      case 'mp4':
      case 'webm':
      case 'mov':
        return <VideoFile sx={{ color: '#1E88E5' }} />;
      case 'mp3':
      case 'wav':
      case 'ogg':
        return <AudioFile sx={{ color: '#8E24AA' }} />;
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'html':
      case 'css':
        return <Code sx={{ color: '#FDD835' }} />;
      case 'txt':
      case 'doc':
      case 'docx':
        return <Description sx={{ color: '#1565C0' }} />;
      default:
        return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
    }
  };

  const isImageFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const handlePreview = async (filename) => {
    if (isImageFile(filename)) {
      try {
        const fullPath = currentPath ? `${currentPath}/${filename}` : filename;
        const response = await fileManagementApi.downloadFile(fullPath, true);
        setPreviewDialog({
          open: true,
          file: response,
          originalName: filename // Store the original filename for the header
        });
      } catch (err) {
        setError('Failed to load image preview');
      }
    }
  };

  const handleDownload = async (filename) => {
    try {
      const fullPath = currentPath ? `${currentPath}/${filename}` : filename;
      await fileManagementApi.downloadFile(fullPath);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.file) return;

    try {
      const fullPath = currentPath ? `${currentPath}/${deleteDialog.file}` : deleteDialog.file;
      await fileManagementApi.deleteFile(fullPath);
      setDeleteDialog({ open: false, file: null, confirmText: '' });
      fetchFiles();
    } catch (err) {
      setError('Failed to delete file');
    }
  };

  const handleFolderClick = (folderName) => {
    setCurrentPath(currentPath ? `${currentPath}/${folderName}` : folderName);
  };

  const handleBreadcrumbClick = (path) => {
    setCurrentPath(path);
  };

  const handleBackClick = () => {
    const pathParts = currentPath.split('/');
    pathParts.pop();
    setCurrentPath(pathParts.join('/'));
  };

  const renderBreadcrumbs = () => {
    const pathParts = currentPath.split('/').filter(Boolean);
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          borderRadius: 2,
          flex: 1,
        }}
      >
        <Link
          component="button"
          onClick={() => setCurrentPath('')}
          sx={{
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <Folder fontSize="small" />
          Root
        </Link>
        {pathParts.length > 0 && (
          <Typography color="text.secondary" sx={{ mx: 1 }}>
            /
          </Typography>
        )}
        {pathParts.map((part, index) => {
          const path = pathParts.slice(0, index + 1).join('/');
          const isLast = index === pathParts.length - 1;

          return (
            <React.Fragment key={path}>
              <Link
                component="button"
                onClick={() => handleBreadcrumbClick(path)}
                sx={{
                  color: isLast ? 'text.primary' : 'primary.main',
                  fontWeight: isLast ? 500 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  cursor: isLast ? 'default' : 'pointer',
                  '&:hover': {
                    textDecoration: isLast ? 'none' : 'underline',
                  },
                }}
              >
                {part}
              </Link>
              {!isLast && (
                <Typography color="text.secondary" sx={{ mx: 1 }}>
                  /
                </Typography>
              )}
            </React.Fragment>
          );
        })}
      </Paper>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: (theme) =>
            `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(
              theme.palette.primary.main,
              0.05
            )})`,
          borderRadius: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <FolderRounded sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              File Manager
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your uploaded files and folders
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        {currentPath && (
          <IconButton
            onClick={handleBackClick}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <ArrowBack />
          </IconButton>
        )}
        {renderBreadcrumbs()}
      </Box>
      {/* Search Bar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          placeholder="Search files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ width: 300 }}
        />
      </Box>

      {/* File List */}
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files
              .filter((file) =>
                file.name.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((file) => (
                <TableRow
                  key={file.name}
                  hover
                  sx={{
                    cursor: file.type === 'folder' || isImageFile(file.name) ? 'pointer' : 'default',
                    '&:last-child td, &:last-child th': { border: 0 },
                  }}
                  onClick={(e) => {
                    if (file.type === 'folder') {
                      handleFolderClick(file.name);
                    } else if (isImageFile(file.name)) {
                      handlePreview(file.name);
                    }
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: isImageFile(file.name) ? 'primary.main' : 'inherit'
                      }}
                    >
                      {file.type === 'folder' ? (
                        <Folder sx={{ color: 'primary.main' }} />
                      ) : (
                        getFileIcon(file.name)
                      )}
                      {file.name}
                      {isImageFile(file.name)}
                    </Box>
                  </TableCell>
                  <TableCell>{file.type}</TableCell>
                  <TableCell align="right">
                    {file.type !== 'folder' && (
                      <Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(file.name);
                          }}
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialog({ open: true, file: file.name, confirmText: '' });
                          }}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, file: null, confirmText: '' })}
        PaperProps={{
          sx: {
            borderTop: '4px solid',
            borderColor: 'warning.main',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this file? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <InsertDriveFile fontSize="small" />
            {deleteDialog.file}
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
            Type DELETE in all caps to confirm:
          </Typography>
          <TextField
            fullWidth
            value={deleteDialog.confirmText}
            onChange={(e) => setDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }))}
            size="small"
            sx={{ mb: 1 }}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, file: null, confirmText: '' })}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={<Delete />}
            disabled={deleteDialog.confirmText !== 'DELETE'}
          >
            Delete File
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Overlay */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, file: null })}
        maxWidth={false}
        fullWidth
        sx={{
          '.MuiBackdrop-root': {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
          '.MuiDialog-paper': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            margin: 0,
            maxHeight: '100vh',
            height: '100vh',
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            height: 64,
            px: 2,
            zIndex: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          <IconButton
            onClick={() => setPreviewDialog({ open: false, file: null })}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
              mr: 2,
            }}
          >
            <ArrowBack />
          </IconButton>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: 1,
          }}>
            <ImageIcon sx={{ color: 'primary.main' }} />
            <Typography sx={{
              fontSize: '1rem',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.95)',
            }}>
              {previewDialog.file && decodeURIComponent(previewDialog.originalName || '')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(previewDialog.originalName);
              }}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Download />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setDeleteDialog({ open: true, file: previewDialog.originalName });
                setPreviewDialog({ open: false, file: null });
              }}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>

        {/* Image Container */}
        <Box
          onClick={() => setPreviewDialog({ open: false, file: null })}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pt: 10,
            px: 3,
            pb: 3,
          }}
        >
          {previewDialog.file && (
            <Box
              component="img"
              src={previewDialog.file}
              alt="Preview"
              onClick={(e) => e.stopPropagation()}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                cursor: 'default',
                borderRadius: 1,
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              }}
            />
          )}
        </Box>
      </Dialog>

      {error && (
        <Paper sx={{ p: 2, m: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          {error}
        </Paper>
      )}
    </Box>
  );
};

export default FileManager;